import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Invoice, RecordPaymentData } from '@/types/invoice.types';
import { invoicesService } from '@/services/invoices.service';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { InvoiceStatusBadge } from '@/components/billing';

/**
 * Format amount in Indian currency format (lakhs/crores)
 */
const formatIndianCurrency = (amount: number): string => {
  const formatter = new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return formatter.format(amount);
};

const formatDate = (dateString: string): string => {
  return new Date(dateString).toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

const getInvoiceTypeLabel = (type: string): string => {
  const labels: Record<string, string> = {
    DEVELOPER: 'Developer',
    INDEPENDENT: 'Independent',
    BROKER_REFERRAL: 'Broker Referral',
  };
  return labels[type] || type;
};

const paymentMethods = [
  { value: 'BANK_TRANSFER', label: 'Bank Transfer' },
  { value: 'UPI', label: 'UPI' },
  { value: 'CHEQUE', label: 'Cheque' },
  { value: 'CASH', label: 'Cash' },
];

export const InvoiceDetail: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const [invoice, setInvoice] = useState<Invoice | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Payment modal state
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentData, setPaymentData] = useState<RecordPaymentData>({
    amount: 0,
    payment_date: new Date().toISOString().split('T')[0],
    payment_method: '',
    reference_number: '',
  });
  const [isSubmittingPayment, setIsSubmittingPayment] = useState(false);
  const [paymentError, setPaymentError] = useState<string | null>(null);

  // Cancel modal state
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [isCancelling, setIsCancelling] = useState(false);

  const fetchInvoice = useCallback(async () => {
    if (!id) return;

    try {
      setIsLoading(true);
      setError(null);
      const response = await invoicesService.getById(id);
      // API interceptor unwraps { success: true, data: {...} } to just the data
      // Backend returns { invoice: {...} } so extract the invoice object
      const invoiceData = response.data?.invoice || response.data;
      setInvoice(invoiceData);
    } catch (err) {
      setError('Failed to load invoice');
      console.error('Error fetching invoice:', err);
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchInvoice();
  }, [fetchInvoice]);

  const handleRecordPayment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!invoice) return;

    setPaymentError(null);

    if (!paymentData.amount || paymentData.amount <= 0) {
      setPaymentError('Please enter a valid amount');
      return;
    }

    if (!paymentData.payment_method) {
      setPaymentError('Please select a payment method');
      return;
    }

    try {
      setIsSubmittingPayment(true);
      await invoicesService.recordPayment(invoice.id, paymentData);
      setShowPaymentModal(false);
      setPaymentData({
        amount: 0,
        payment_date: new Date().toISOString().split('T')[0],
        payment_method: '',
        reference_number: '',
      });
      fetchInvoice();
    } catch (err) {
      setPaymentError('Failed to record payment. Please try again.');
      console.error('Error recording payment:', err);
    } finally {
      setIsSubmittingPayment(false);
    }
  };

  const handleCancelInvoice = async () => {
    if (!invoice || !cancelReason.trim()) return;

    try {
      setIsCancelling(true);
      await invoicesService.cancel(invoice.id, cancelReason);
      setShowCancelModal(false);
      setCancelReason('');
      fetchInvoice();
    } catch (err) {
      console.error('Error cancelling invoice:', err);
    } finally {
      setIsCancelling(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="flex flex-col items-center space-y-4">
          <div className="w-12 h-12 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin"></div>
          <p className="text-secondary-600">Loading invoice...</p>
        </div>
      </div>
    );
  }

  if (error || !invoice) {
    return (
      <div className="text-center py-12">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-red-100 mb-4">
          <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <h3 className="text-lg font-medium text-secondary-900 mb-2">Failed to load invoice</h3>
        <p className="text-secondary-600 mb-4">{error || 'Invoice not found'}</p>
        <Button onClick={() => navigate('/billing')}>Back to Billing</Button>
      </div>
    );
  }

  const paidAmount = invoice.payments.reduce((sum, p) => sum + p.amount, 0);
  const outstandingAmount = invoice.total_amount - paidAmount;
  const canRecordPayment = invoice.status !== 'PAID' && invoice.status !== 'CANCELLED';
  const canCancel = invoice.status !== 'PAID' && invoice.status !== 'CANCELLED';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/billing')}
            className="p-2 hover:bg-secondary-100 rounded-lg transition-colors"
          >
            <svg
              className="w-5 h-5 text-secondary-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
          </button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-secondary-900">
                {invoice.invoice_number}
              </h1>
              <InvoiceStatusBadge status={invoice.status} />
            </div>
            <p className="text-secondary-600 mt-1">
              Issued on {formatDate(invoice.issued_date)}
            </p>
          </div>
        </div>
        <div className="flex gap-3">
          {canCancel && (
            <Button variant="danger" onClick={() => setShowCancelModal(true)}>
              Cancel Invoice
            </Button>
          )}
          {canRecordPayment && (
            <Button onClick={() => setShowPaymentModal(true)}>
              Record Payment
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Bill To & Invoice Details */}
          <Card>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-sm font-medium text-secondary-500 uppercase tracking-wider mb-2">
                  Bill To
                </h3>
                <p className="text-lg font-semibold text-secondary-900">
                  {invoice.bill_to_name}
                </p>
                {invoice.bill_to_gstin && (
                  <p className="text-sm text-secondary-600 mt-1">
                    GSTIN: {invoice.bill_to_gstin}
                  </p>
                )}
              </div>
              <div>
                <h3 className="text-sm font-medium text-secondary-500 uppercase tracking-wider mb-2">
                  Invoice Details
                </h3>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-secondary-600">Type:</span>
                    <span className="font-medium text-secondary-900">
                      {getInvoiceTypeLabel(invoice.type)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-secondary-600">Lead:</span>
                    <span className="font-medium text-secondary-900">
                      {invoice.lead.name}
                    </span>
                  </div>
                  {invoice.due_date && (
                    <div className="flex justify-between">
                      <span className="text-secondary-600">Due Date:</span>
                      <span className="font-medium text-secondary-900">
                        {formatDate(invoice.due_date)}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </Card>

          {/* Line Items / Calculation Breakdown */}
          <Card>
            <h3 className="text-lg font-semibold text-secondary-900 mb-4">
              Calculation Breakdown
            </h3>
            <div className="space-y-3">
              <div className="flex justify-between items-center py-2 border-b border-secondary-100">
                <span className="text-secondary-600">Booking Value</span>
                <span className="font-medium text-secondary-900">
                  {formatIndianCurrency(invoice.booking_value)}
                </span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-secondary-100">
                <span className="text-secondary-600">
                  Brokerage @ {invoice.brokerage_percent}%
                </span>
                <span className="font-medium text-secondary-900">
                  {formatIndianCurrency(invoice.base_amount)}
                </span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-secondary-100">
                <span className="text-secondary-600">GST @ 18%</span>
                <span className="font-medium text-green-600">
                  + {formatIndianCurrency(invoice.gst_amount)}
                </span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-secondary-100">
                <span className="text-secondary-600">TDS @ 1%</span>
                <span className="font-medium text-red-600">
                  - {formatIndianCurrency(invoice.tds_amount)}
                </span>
              </div>
              <div className="flex justify-between items-center py-3 bg-secondary-50 -mx-6 px-6 rounded-b-xl mt-4">
                <span className="text-lg font-semibold text-secondary-900">Total Amount</span>
                <span className="text-xl font-bold text-primary-600">
                  {formatIndianCurrency(invoice.total_amount)}
                </span>
              </div>
            </div>
          </Card>

          {/* Payment History */}
          <Card>
            <h3 className="text-lg font-semibold text-secondary-900 mb-4">
              Payment History
            </h3>
            {invoice.payments.length === 0 ? (
              <div className="text-center py-6">
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-secondary-100 mb-3">
                  <svg
                    className="w-6 h-6 text-secondary-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
                    />
                  </svg>
                </div>
                <p className="text-secondary-600">No payments recorded yet</p>
              </div>
            ) : (
              <div className="space-y-3">
                {invoice.payments.map((payment) => (
                  <div
                    key={payment.id}
                    className="flex items-center justify-between p-4 bg-secondary-50 rounded-lg"
                  >
                    <div>
                      <p className="font-medium text-secondary-900">
                        {formatIndianCurrency(payment.amount)}
                      </p>
                      <p className="text-sm text-secondary-500">
                        {payment.payment_method} | {formatDate(payment.payment_date)}
                      </p>
                      {payment.reference_number && (
                        <p className="text-xs text-secondary-400 mt-1">
                          Ref: {payment.reference_number}
                        </p>
                      )}
                    </div>
                    <div className="p-2 bg-green-100 rounded-full">
                      <svg
                        className="w-5 h-5 text-green-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>

        {/* Sidebar */}
        <div className="lg:col-span-1 space-y-6">
          {/* Payment Summary */}
          <Card>
            <h3 className="text-lg font-semibold text-secondary-900 mb-4">
              Payment Summary
            </h3>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-secondary-600">Total Amount</span>
                <span className="font-medium text-secondary-900">
                  {formatIndianCurrency(invoice.total_amount)}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-secondary-600">Paid</span>
                <span className="font-medium text-green-600">
                  {formatIndianCurrency(paidAmount)}
                </span>
              </div>
              <div className="border-t border-secondary-200 pt-3">
                <div className="flex justify-between items-center">
                  <span className="font-semibold text-secondary-900">Outstanding</span>
                  <span className={`font-bold ${outstandingAmount > 0 ? 'text-red-600' : 'text-green-600'}`}>
                    {formatIndianCurrency(outstandingAmount)}
                  </span>
                </div>
              </div>
            </div>

            {/* Progress bar */}
            <div className="mt-4">
              <div className="h-2 bg-secondary-200 rounded-full overflow-hidden">
                <div
                  className="h-full bg-green-500 transition-all duration-300"
                  style={{ width: `${Math.min((paidAmount / invoice.total_amount) * 100, 100)}%` }}
                ></div>
              </div>
              <p className="text-xs text-secondary-500 mt-2 text-center">
                {Math.round((paidAmount / invoice.total_amount) * 100)}% paid
              </p>
            </div>
          </Card>

          {/* Quick Actions */}
          {canRecordPayment && (
            <Card>
              <h3 className="text-lg font-semibold text-secondary-900 mb-4">
                Quick Actions
              </h3>
              <div className="space-y-3">
                <Button
                  fullWidth
                  onClick={() => {
                    setPaymentData({ ...paymentData, amount: outstandingAmount });
                    setShowPaymentModal(true);
                  }}
                >
                  Record Full Payment
                </Button>
                <Button
                  variant="outline"
                  fullWidth
                  onClick={() => setShowPaymentModal(true)}
                >
                  Record Partial Payment
                </Button>
              </div>
            </Card>
          )}
        </div>
      </div>

      {/* Payment Modal */}
      {showPaymentModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <div
              className="fixed inset-0 bg-black bg-opacity-50"
              onClick={() => setShowPaymentModal(false)}
            ></div>
            <div className="relative bg-white rounded-xl shadow-lg w-full max-w-md p-6">
              <h3 className="text-lg font-semibold text-secondary-900 mb-4">
                Record Payment
              </h3>
              <form onSubmit={handleRecordPayment} className="space-y-4">
                <Input
                  label="Amount"
                  type="number"
                  placeholder="Enter amount"
                  value={paymentData.amount || ''}
                  onChange={(e) =>
                    setPaymentData({ ...paymentData, amount: Number(e.target.value) })
                  }
                  leftIcon={<span className="text-secondary-500">Rs</span>}
                  helperText={`Outstanding: ${formatIndianCurrency(outstandingAmount)}`}
                />
                <Input
                  label="Payment Date"
                  type="date"
                  value={paymentData.payment_date}
                  onChange={(e) =>
                    setPaymentData({ ...paymentData, payment_date: e.target.value })
                  }
                />
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">
                    Payment Method
                  </label>
                  <select
                    value={paymentData.payment_method}
                    onChange={(e) =>
                      setPaymentData({ ...paymentData, payment_method: e.target.value })
                    }
                    className="w-full px-4 py-2 border border-secondary-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="">Select payment method</option>
                    {paymentMethods.map((method) => (
                      <option key={method.value} value={method.value}>
                        {method.label}
                      </option>
                    ))}
                  </select>
                </div>
                <Input
                  label="Reference Number (Optional)"
                  placeholder="Transaction ID, cheque number, etc."
                  value={paymentData.reference_number || ''}
                  onChange={(e) =>
                    setPaymentData({ ...paymentData, reference_number: e.target.value })
                  }
                />

                {paymentError && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-sm text-red-600">{paymentError}</p>
                  </div>
                )}

                <div className="flex gap-3 pt-2">
                  <Button
                    type="button"
                    variant="outline"
                    fullWidth
                    onClick={() => setShowPaymentModal(false)}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" fullWidth isLoading={isSubmittingPayment}>
                    Record Payment
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Cancel Modal */}
      {showCancelModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <div
              className="fixed inset-0 bg-black bg-opacity-50"
              onClick={() => setShowCancelModal(false)}
            ></div>
            <div className="relative bg-white rounded-xl shadow-lg w-full max-w-md p-6">
              <h3 className="text-lg font-semibold text-secondary-900 mb-4">
                Cancel Invoice
              </h3>
              <p className="text-secondary-600 mb-4">
                Are you sure you want to cancel this invoice? This action cannot be undone.
              </p>
              <div className="mb-4">
                <label className="block text-sm font-medium text-secondary-700 mb-2">
                  Reason for cancellation
                </label>
                <textarea
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  placeholder="Enter reason..."
                  className="w-full px-4 py-2 border border-secondary-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  rows={3}
                />
              </div>
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  fullWidth
                  onClick={() => setShowCancelModal(false)}
                >
                  Keep Invoice
                </Button>
                <Button
                  variant="danger"
                  fullWidth
                  onClick={handleCancelInvoice}
                  isLoading={isCancelling}
                  disabled={!cancelReason.trim()}
                >
                  Cancel Invoice
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InvoiceDetail;
